import { useEffect, useState } from 'react'
import { Badge, Modal, Button, Spinner, Table } from 'react-bootstrap'
import { zefixAuditLogs } from '../../service/zefix'
import { t } from '../../locale'
import { AuditLogEntry, ZefixAuditLogModalProps } from '../../types/zefix.type'

const ACTION_LABELS: Record<AuditLogEntry['action'], () => string> = {
  search: () => t('zefixAuditActionSearch'),
  details: () => t('zefixAuditActionDetails'),
  view: () => t('zefixAuditActionView'),
  change: () => t('zefixAuditActionChange'),
}

const ACTION_VARIANTS: Record<AuditLogEntry['action'], string> = {
  search: 'info',
  details: 'primary',
  view: 'secondary',
  change: 'warning',
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function ZefixAuditLogModal({ show, clientId, onClose }: ZefixAuditLogModalProps) {
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!show || !clientId) return

    const loadLogs = async () => {
      try {
        setLoading(true)
        const data = await zefixAuditLogs(clientId)
        setLogs(Array.isArray(data) ? data : [])
      } catch (err) {
        console.error('Fehler beim Laden der Zefix-Protokolle:', err)
      } finally {
        setLoading(false)
      }
    }

    loadLogs()
  }, [show, clientId])

  return (
    <Modal show={show} centered size='lg' onHide={onClose}>
      <Modal.Header className='bg-secondary text-white' closeButton>
        <Modal.Title className='fs-5'>{t('zefixAuditLogTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='zefix-audit-modal__body'>
        {loading && (
          <div className='d-flex justify-content-center py-4'>
            <Spinner animation='border' variant='primary' />
          </div>
        )}

        {!loading && logs.length === 0 && (
          <p className='text-muted text-center py-3'>{t('zefixAuditNoLogs')}</p>
        )}

        {!loading && logs.length > 0 && (
          <Table striped bordered hover size='sm' responsive>
            <thead>
              <tr>
                <th>{t('zefixAuditDate')}</th>
                <th>{t('zefixAuditAction')}</th>
                <th>{t('zefixAuditUser')}</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log._id}>
                  <td className='text-nowrap'>{formatDate(log.createdAt)}</td>
                  <td>
                    <Badge bg={ACTION_VARIANTS[log.action]}>{ACTION_LABELS[log.action]()}</Badge>
                  </td>
                  <td>{log.userEmail}</td>
                  <td>
                    {log.action === 'search' && log.searchQuery && (
                      <span>&laquo;{log.searchQuery}&raquo;</span>
                    )}
                    {log.companyName && (
                      <span>
                        {log.companyName}
                        {log.uid ? ` (${log.uid})` : ''}
                      </span>
                    )}
                    {log.action === 'change' && log.changes && log.changes.length > 0 && (
                      <Table size='sm' className='mb-0 mt-1' borderless>
                        <thead>
                          <tr>
                            <th className='zefix-audit__th'>{t('zefixAuditField')}</th>
                            <th className='zefix-audit__th'>{t('zefixAuditOldValue')}</th>
                            <th className='zefix-audit__th'>{t('zefixAuditNewValue')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {log.changes.map((change, idx) => (
                            <tr key={idx}>
                              <td>
                                <strong>{change.field}</strong>
                              </td>
                              <td className='text-danger'>{change.oldValue ?? '-'}</td>
                              <td className='text-success'>{change.newValue ?? '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          {t('close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ZefixAuditLogModal
